import { Link } from "react-router-dom";

/**
 * Site-wide footer, rendered on every page. Organized into four labelled
 * columns (Play / Events / Club / Visit) so every live page is reachable — and
 * crawlable without JavaScript — from any URL. Each column is its own <nav> with
 * an aria-label; the header's primary nav lives in a Radix Sheet on mobile and
 * is not always in the server-rendered HTML, so these links are the crawl graph.
 *
 * All 9 keyword landing pages plus the core, events, blog, and press pages are
 * linked here. Kept lean: clean labelled links, no keyword stuffing.
 */
const linkClass = "hover:text-white/80 transition-colors";
const colHeading =
  "font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider text-[hsl(var(--velto-copper))] mb-3";

const columns: { label: string; links: { label: string; to: string }[] }[] = [
  {
    label: "Play",
    links: [
      { label: "Book a court", to: "/book" },
      { label: "Indoor courts", to: "/indoor-pickleball-courts-brooklyn" },
      { label: "Open play", to: "/pickleball-open-play-brooklyn" },
      { label: "Lessons & coaching", to: "/pickleball-lessons-brooklyn" },
      { label: "Leagues", to: "/pickleball-leagues-brooklyn" },
      { label: "Skill levels", to: "/levels" },
    ],
  },
  {
    label: "Events",
    links: [
      { label: "Plan an event", to: "/events" },
      { label: "Private events", to: "/private-pickleball-events-brooklyn" },
      { label: "Corporate events", to: "/corporate-pickleball-events-nyc" },
      { label: "Birthday parties", to: "/pickleball-birthday-party-brooklyn" },
      { label: "Court rental for groups", to: "/pickleball-court-rental-brooklyn" },
    ],
  },
  {
    label: "Club",
    links: [
      { label: "Packages", to: "/pickleball-packages-brooklyn" },
      { label: "Guides", to: "/blog" },
      { label: "About", to: "/about" },
      { label: "Press", to: "/press" },
    ],
  },
];

const Footer = () => {
  return (
    <footer
      className="w-full"
      style={{ backgroundColor: "hsl(var(--velto-forest))", color: "hsl(var(--velto-beige))" }}
    >
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 font-sweet text-sm">
          {columns.map((col) => (
            <nav key={col.label} aria-label={col.label}>
              <p className={colHeading}>{col.label}</p>
              <ul className="space-y-2 text-[hsl(var(--velto-beige))]/80">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className={linkClass}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Visit: location / hours / contact / FAQ / policies */}
          <nav aria-label="Visit">
            <p className={colHeading}>Visit</p>
            <p className="font-instrument text-lg mb-2 text-[hsl(var(--velto-beige))]">Velto Pickleball Club</p>
            <address className="not-italic leading-relaxed text-[hsl(var(--velto-beige))]/80">
              160 Van Brunt Street<br />
              Brooklyn, NY 11231
            </address>
            <p className="mt-2 text-[hsl(var(--velto-beige))]/80">Open daily 6:00 AM – 12:00 AM</p>
            <ul className="mt-3 space-y-2 text-[hsl(var(--velto-beige))]/80">
              <li>
                <a className={`underline ${linkClass}`} href="tel:+15169685204">(516) 968-5204</a>
              </li>
              <li>
                General:{" "}
                <a className={`underline ${linkClass}`} href="mailto:support@veltopb.com">support@veltopb.com</a>
              </li>
              <li>
                Events:{" "}
                <a className={`underline ${linkClass}`} href="mailto:events@veltopb.com">events@veltopb.com</a>
              </li>
              <li><Link to="/contact" className={linkClass}>Contact</Link></li>
              <li><Link to="/faqs" className={linkClass}>FAQs</Link></li>
              <li><Link to="/privacy-policy" className={linkClass}>Privacy Policy</Link></li>
              <li><Link to="/terms-of-use" className={linkClass}>Terms of Use</Link></li>
            </ul>
          </nav>
        </div>

        <div className="mt-10 pt-6 border-t border-[hsl(var(--velto-beige))]/15 text-sm text-[hsl(var(--velto-beige))]/70">
          <p className="font-sweet">
            © {new Date().getFullYear()} Velto Pickleball Club. All Rights Reserved.
          </p>
          <p className="font-sweet mt-1">
            Operated by O-O-2 PICKLEBALL USA LLC, doing business as Velto Pickleball Club, Brooklyn, New York.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
